<script setup lang="ts">
import PublicationDialog from '@/views/external-launch/PublicationDialog.vue'
import { downloadPublicationArtifact } from '@/views/external-launch/downloadArtifact'
import type {
  ConnectionData,
  PublicationData,
  PublicationStatus,
  ScenarioOption,
} from '@/views/external-launch/types'
import { formatDateToMonthShort } from '@core/utils/formatters'
import { getDateLocale } from '@core/utils/locale'

// Менеджер клиента публикует свои сценарии сам, но до секретов системы обучения не допущен:
// `read games` есть и у него, и у администраторов платформы.
definePage({
  meta: {
    action: 'read',
    subject: 'games',
  },
})

const { t } = useI18n()
const { locale } = useI18n({ useScope: 'global' })
const router = useRouter()
const dateLocale = getDateLocale(locale.value)
const userData = useCookie<any>('userData')

const userTimeZone = computed(
  () => userData.value?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC',
)

// Тот же снапшот признака, что гейтит пункт меню. Не защита — бэкенд на выключенном признаке
// отвечает 403, — а чтобы прямой заход по адресу не показывал пустой раздел.
const orgSettings = useCookie<any>('organizationSettings')

if (!(orgSettings.value?.enabledFeatures ?? []).includes('external-launch'))
  await router.replace('/games/list')

const publications = ref<PublicationData[]>([])
const connections = ref<ConnectionData[]>([])
const scenarios = ref<ScenarioOption[]>([])
const isLoading = ref(true)
const loadFailed = ref(false)

const isSnackbarVisible = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('success')

const isDialogVisible = ref(false)
const activePublication = ref<PublicationData | null>(null)
const downloadingId = ref<string | null>(null)

const headers = [
  { title: t('externalLaunch.manage.publications.headers.scenario'), key: 'scenario', sortable: false },
  { title: t('externalLaunch.manage.publications.headers.connection'), key: 'connection', sortable: false },
  { title: t('externalLaunch.manage.publications.headers.channel'), key: 'channel' },
  { title: t('externalLaunch.manage.publications.headers.status'), key: 'status' },
  { title: t('externalLaunch.manage.publications.headers.settings'), key: 'settings', sortable: false },
  { title: t('externalLaunch.manage.publications.headers.expires_at'), key: 'expiresAt' },
  { title: t('externalLaunch.manage.publications.headers.actions'), key: 'actions', sortable: false },
]

const notify = (text: string, color: string = 'success') => {
  snackbarText.value = text
  snackbarColor.value = color
  isSnackbarVisible.value = true
}

const fetchPublications = async () => {
  isLoading.value = true
  loadFailed.value = false

  try {
    publications.value = (await $api<PublicationData[]>('/external-launch/publications')) ?? []
  }
  catch {
    publications.value = []
    loadFailed.value = true
  }
  finally {
    isLoading.value = false
  }
}

// Подключения менеджеру недоступны (секрет системы обучения даёт доступ к данным учеников),
// и 403 здесь — штатный ответ, а не поломка: список остаётся пустым, а выбор подключения в
// диалоге создания — единственное, что без него не работает.
const fetchConnections = async () => {
  try {
    connections.value = (await $api<ConnectionData[]>('/external-launch/connections')) ?? []
  }
  catch {
    connections.value = []
  }
}

const fetchScenarios = async () => {
  try {
    scenarios.value = (await $api<ScenarioOption[]>('/scenarios/all-for-ai')) ?? []
  }
  catch {
    scenarios.value = []
  }
}

await Promise.all([fetchPublications(), fetchConnections(), fetchScenarios()])

const scenarioTitleById = computed(() => {
  const map = new Map<string, string>()

  for (const scenario of scenarios.value)
    map.set(String(scenario.id), scenario.title)

  return map
})

const connectionLabelById = computed(() => {
  const map = new Map<string, string>()

  for (const connection of connections.value)
    map.set(String(connection.id), connection.accountKey)

  return map
})

const scenarioTitle = (publication: PublicationData): string =>
  scenarioTitleById.value.get(String(publication.scenarioId)) ?? '—'

const connectionLabel = (publication: PublicationData): string =>
  connectionLabelById.value.get(String(publication.connectionId)) ?? '—'

const statusColor = (status: PublicationStatus): string => {
  if (status === 'active')
    return 'success'
  if (status === 'disabled')
    return 'secondary'

  return 'warning'
}

const statusLabel = (status: PublicationStatus): string =>
  t(`externalLaunch.manage.publications.status.${status}`)

const channelLabel = (publication: PublicationData): string =>
  t(`externalLaunch.manage.publications.channel.${publication.channel}`)

const attemptsLabel = (publication: PublicationData): string =>
  publication.settings.attemptsAllowed === null
    ? t('externalLaunch.manage.publications.attempts_unlimited')
    : String(publication.settings.attemptsAllowed)

const expiresLabel = (publication: PublicationData): string =>
  publication.expiresAt
    ? formatDateToMonthShort(publication.expiresAt, false, dateLocale, userTimeZone.value)
    : t('externalLaunch.manage.publications.no_expiry')

const openCreate = () => {
  activePublication.value = null
  isDialogVisible.value = true
}

const openEdit = (publication: PublicationData) => {
  activePublication.value = publication
  isDialogVisible.value = true
}

// Пакет собирается заново на каждое скачивание, и ключ внутри — тот же, что выдан при создании:
// менеджер, закрывший диалог ключа, возвращает его отсюда, не заводя публикацию заново.
const downloadArtifact = async (publication: PublicationData) => {
  downloadingId.value = String(publication.id)

  try {
    await downloadPublicationArtifact(String(publication.id))
  }
  catch {
    notify(t('externalLaunch.manage.publications.messages.download_failed'), 'error')
  }
  finally {
    downloadingId.value = null
  }
}

const onSaved = async () => {
  notify(t('externalLaunch.manage.publications.messages.saved'))
  await fetchPublications()
}

// Включение и выключение — то же изменение публикации, только со сменой одного поля: ручка
// принимает набор целиком, и настройки приходится переслать как есть, иначе они потерялись бы.
const setStatus = async (publication: PublicationData, next: PublicationStatus) => {
  try {
    await $api(`/external-launch/publications/${publication.id}`, {
      method: 'PUT',
      body: {
        status: next,
        settings: {
          attempts_allowed: publication.settings.attemptsAllowed,
          daily_launch_budget: publication.settings.dailyLaunchBudget,
          result_mode: publication.settings.resultMode,
          pass_threshold: publication.settings.passThreshold,
          result_wait_hours: publication.settings.resultWaitHours,
        },
        require_assignment_check: publication.requireAssignmentCheck,
        external_course_id: publication.externalCourseId,
        external_item_id: publication.externalItemId,
        expires_at: publication.expiresAt,
        launch_limit: publication.launchLimit,
      },
    })

    notify(next === 'disabled'
      ? t('externalLaunch.manage.publications.messages.disabled')
      : t('externalLaunch.manage.publications.messages.enabled'))

    await fetchPublications()
  }
  catch {
    notify(t('externalLaunch.manage.messages.error'), 'error')
  }
}
</script>

<template>
  <div>
    <div class="d-flex justify-space-between align-center flex-wrap gap-4 mb-4">
      <div>
        <h4 class="text-h4">
          {{ t('externalLaunch.manage.publications.title') }}
        </h4>
        <div class="text-body-2 text-medium-emphasis">
          {{ t('externalLaunch.manage.publications.subtitle') }}
        </div>
      </div>
      <VBtn
        prepend-icon="tabler-plus"
        :disabled="!connections.length"
        @click="openCreate"
      >
        {{ t('externalLaunch.manage.publications.create') }}
      </VBtn>
    </div>

    <VAlert
      v-if="!connections.length"
      type="info"
      variant="tonal"
      class="mb-4"
    >
      {{ t('externalLaunch.manage.publications.no_connections') }}
    </VAlert>

    <VCard>
      <VAlert
        v-if="loadFailed"
        type="error"
        variant="tonal"
        class="ma-4"
      >
        <div class="d-flex align-center justify-space-between flex-wrap gap-3">
          <span>{{ t('externalLaunch.manage.messages.load_error') }}</span>
          <VBtn
            size="small"
            variant="tonal"
            color="error"
            @click="fetchPublications"
          >
            {{ t('retry') }}
          </VBtn>
        </div>
      </VAlert>

      <VDataTable
        v-else
        :headers="headers"
        :items="publications"
        :loading="isLoading"
        :loading-text="t('loading')"
        :items-per-page="25"
        class="text-no-wrap"
      >
        <template #item.scenario="{ item }">
          <RouterLink
            :to="{ name: 'external-launch-journal-publication', params: { publication: String(item.id) } }"
            class="font-weight-medium text-link"
          >
            {{ scenarioTitle(item) }}
          </RouterLink>
        </template>

        <template #item.connection="{ item }">
          {{ connectionLabel(item) }}
        </template>

        <template #item.channel="{ item }">
          {{ channelLabel(item) }}
        </template>

        <template #item.status="{ item }">
          <VChip
            :color="statusColor(item.status)"
            size="small"
          >
            {{ statusLabel(item.status) }}
          </VChip>
        </template>

        <template #item.settings="{ item }">
          <div class="text-body-2">
            {{ t('externalLaunch.manage.publications.summary.attempts', { value: attemptsLabel(item) }) }}
          </div>
          <div class="text-body-2 text-medium-emphasis">
            {{ t('externalLaunch.manage.publications.summary.threshold', { value: item.settings.passThreshold }) }}
          </div>
        </template>

        <template #item.expiresAt="{ item }">
          {{ expiresLabel(item) }}
        </template>

        <template #item.actions="{ item }">
          <div class="d-flex justify-end">
            <IconBtn :to="{ name: 'external-launch-journal-publication', params: { publication: String(item.id) } }">
              <VTooltip
                location="top"
                activator="parent"
              >
                {{ t('externalLaunch.manage.publications.open_journal') }}
              </VTooltip>
              <VIcon icon="tabler-list-details" />
            </IconBtn>
            <IconBtn
              v-if="item.artifactDownloadable"
              :loading="downloadingId === String(item.id)"
              @click="downloadArtifact(item)"
            >
              <VTooltip
                location="top"
                activator="parent"
              >
                {{ t('externalLaunch.manage.publications.download_package') }}
              </VTooltip>
              <VIcon icon="tabler-download" />
            </IconBtn>
            <IconBtn @click="openEdit(item)">
              <VTooltip
                location="top"
                activator="parent"
              >
                {{ t('externalLaunch.manage.publications.edit') }}
              </VTooltip>
              <VIcon icon="tabler-edit" />
            </IconBtn>
            <IconBtn
              v-if="item.status !== 'disabled'"
              @click="setStatus(item, 'disabled')"
            >
              <VTooltip
                location="top"
                activator="parent"
              >
                {{ t('externalLaunch.manage.publications.disable') }}
              </VTooltip>
              <VIcon icon="tabler-ban" />
            </IconBtn>
            <IconBtn
              v-else
              @click="setStatus(item, 'active')"
            >
              <VTooltip
                location="top"
                activator="parent"
              >
                {{ t('externalLaunch.manage.publications.enable') }}
              </VTooltip>
              <VIcon icon="tabler-player-play" />
            </IconBtn>
          </div>
        </template>

        <template #no-data>
          <div class="text-center text-disabled py-6">
            {{ t('externalLaunch.manage.publications.empty') }}
          </div>
        </template>
      </VDataTable>
    </VCard>

    <PublicationDialog
      v-model:is-dialog-visible="isDialogVisible"
      :publication="activePublication"
      :connections="connections"
      :scenarios="scenarios"
      @saved="onSaved"
    />

    <VSnackbar
      v-model="isSnackbarVisible"
      location="top end"
      :color="snackbarColor"
    >
      {{ snackbarText }}
    </VSnackbar>
  </div>
</template>
